import { RecordInfo } from "./RecordInfo";
import { DeviceProjectAssignment } from "./DeviceProjectAssignment";
import { firestore } from "firebase";
import {
  DEVICE_POWER_INPUT_ATTRIBUTE_TYPES,
  DevicePowerConsumptionInput,
} from "./DeviceAttribute";

export enum ExternalElectricityMeterType {
  COM_BOX = "COM_BOX",
  OTHER = "OTHER"
}

export enum ProjectPhase {
  DRYING = "DRYING",
  FIRST_MEASURE = "FIRST_MEASURE"
}

export type GenericDevicePowerConsumptionInput = DevicePowerConsumptionInput;

export interface ExternalElectricityMeterInfo {
  assignmentEndDate: firestore.Timestamp | null;
  assignmentId: string;
  assignmentStartDate: firestore.Timestamp;
  deviceName: string;
  deviceId: string;
  type: ExternalElectricityMeterType;
}

/**
 * The meter readings of a generic assignment, with who recorded or corrected them.
 *
 * **Despite their names, the two readings are in the device's own unit** (decided 2026-10-01): kWh for
 * an electricity meter, operating hours for an operating-hours device. IRES Office, IRES Cloud's REST
 * API, IRES Project and IRES Work all store them that way, so anything that sums or prints kWh must
 * check the template's power mode first (`selectedPowerConsumptionMode`). See
 * docs/devices/device-assignment/generic-rules.md.
 */
export interface GenericMeterReadingInfo {
  /** Start reading: kWh for an electricity meter, operating hours for an operating-hours device. */
  meterReadingStartKWh?: number | null;
  /** End reading, in the same unit as the start. */
  meterReadingEndKWh?: number | null;

  meterReadingStartCorrectedBy?: RecordInfo | null;
  meterReadingEndCorrectedBy?: RecordInfo | null;
  startDateCorrectedBy?: RecordInfo | null;
  endDateCorrectedBy?: RecordInfo | null;
  meterReadingStartEditedBy?: RecordInfo | null;
  startDateMeterReadingEditedBy?: RecordInfo | null;
}

export interface GenericDeviceProjectAssignment extends DeviceProjectAssignment {
  assignedDeviceId: string;
  assignedDeviceTemplateId: string | null;
  assignedDeviceCategoryId: string | null;
  assignedDeviceName: string;
  input: { 
    [key: string]: string;
  };
  assignedDeviceElectricityMeterManufacturer: string | null;
  assignedDeviceElectricityMeterNumber: string | null;
  createdBy: RecordInfo;
  note: string | null;
  deviceOverviewPageNumber: number | null;
  assignedDeviceInventoryNumber: string | null;
  projectPhase: ProjectPhase;
  meterReadingInfo?: GenericMeterReadingInfo | null;
}

export function extractPowerConsumptionInput(input: { [key: string]: string }): GenericDevicePowerConsumptionInput | null {
  const powerInputKeys = DEVICE_POWER_INPUT_ATTRIBUTE_TYPES;
  const meterReadingString = powerInputKeys
    .map((key) => input[key.toString()])
    .find((value) => !!value);
  
  if (!meterReadingString) {
    return null;
  }

  try {
    const meterReading = JSON.parse(meterReadingString) as GenericDevicePowerConsumptionInput;

    meterReading.startValue = typeof meterReading.startValue === "number" ? meterReading.startValue : null;
    meterReading.endValue = typeof meterReading.endValue === "number" ? meterReading.endValue : null;
    meterReading.userForReport = typeof meterReading.userForReport === 'boolean' ? meterReading.userForReport : undefined;

    return meterReading;
  } catch (e) {
    // Handle parsing error
    return null;
  }
}
